import React, { useEffect, useCallback, useState } from 'react';
import { scanPayloadPlaceholders } from '../utils/payloadScanner';

const STORAGE_KEY = 'payload_shortcut';

export interface ShortcutConfig {
  key: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

export const DEFAULT_SHORTCUT: ShortcutConfig = {
  key: 'i',
  ctrlKey: true,
  shiftKey: false,
  altKey: false,
  metaKey: false,
};

export function shortcutToLabel(s: ShortcutConfig): string {
  const parts: string[] = [];
  if (s.ctrlKey) parts.push('Ctrl');
  if (s.altKey) parts.push('Alt');
  if (s.shiftKey) parts.push('Shift');
  if (s.metaKey) parts.push('Meta');
  parts.push(s.key.length === 1 ? s.key.toUpperCase() : s.key);
  return parts.join('+');
}

export function loadShortcut(): ShortcutConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.key === 'string') return parsed as ShortcutConfig;
    }
  } catch {
    // ignore
  }
  return DEFAULT_SHORTCUT;
}

export function saveShortcut(s: ShortcutConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

function matchesShortcut(e: KeyboardEvent, s: ShortcutConfig): boolean {
  return (
    e.key.toLowerCase() === s.key.toLowerCase() &&
    e.ctrlKey === s.ctrlKey &&
    e.shiftKey === s.shiftKey &&
    e.altKey === s.altKey &&
    e.metaKey === s.metaKey
  );
}

/**
 * Returns the next placeholder number to use, given existing configuration values.
 * Uses scanPayloadPlaceholders which properly excludes payload_functions metadata.
 */
function nextPlaceholderNumber(configRef: React.MutableRefObject<Record<string, any>>): number {
  const ids = scanPayloadPlaceholders(configRef.current);
  return ids.length > 0 ? Math.max(...ids) + 1 : 1;
}

/**
 * Hook that listens for the configured shortcut on any focused text input
 * and inserts a §N§ placeholder at the cursor position.
 *
 * `onInsert(fieldPath, newValue, placeholderNumber)` is called so the parent
 * can update the right field in configuration.
 */
export function usePayloadShortcut(
  configRef: React.MutableRefObject<Record<string, any>>,
  onInsert: (element: HTMLInputElement | HTMLTextAreaElement, placeholderNumber: number) => void,
  containerRef: React.RefObject<HTMLElement>,
) {
  const [shortcut, setShortcutState] = useState<ShortcutConfig>(loadShortcut);

  const updateShortcut = useCallback((s: ShortcutConfig) => {
    saveShortcut(s);
    setShortcutState(s);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!matchesShortcut(e, shortcut)) return;

      const el = document.activeElement;
      if (!el) return;
      if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;

      // Only fire inside our container
      if (containerRef.current && !containerRef.current.contains(el)) return;

      const input = el as HTMLInputElement | HTMLTextAreaElement;
      const inputType = (input as HTMLInputElement).type;
      // Only text-like inputs plus numeric inputs used by config fields.
      if (inputType && !['text', 'url', 'search', 'email', 'password', 'number', '', undefined].includes(inputType) && el.tagName !== 'TEXTAREA') return;

      // Only fire on inputs that have been marked with data-field-name (attack config fields)
      if (!el.getAttribute('data-field-name')) return;

      e.preventDefault();

      const n = nextPlaceholderNumber(configRef);
      onInsert(input, n);
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [shortcut, configRef, onInsert, containerRef]);

  return { shortcut, updateShortcut };
}
